import { Home, Schedule, Settings } from '@otp/icons';
import dayjs from 'dayjs';
import {
  BottomNavigation,
  Button,
  Callout,
  Controller,
  DatePicker,
  Delimiter,
  FormDirtyModal,
  PageModal,
  Switch,
  TimePicker,
  Typography,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { StoresField } from 'src/components/common/form/fields/stores';
import clsx from 'clsx';
import styles from './index.module.css';
import { ActionField } from '../fields/action';
import { DevicesField } from '../fields/devices';
import { CronField } from '../fields/cron';
import {
  MostScheduledJob,
  Form,
  MostActionConfigJobFormProps,
  MostJobConfig,
  MostJobSchedule,
} from './types';
import { DuplicateConfigAlert } from 'src/components/most/list/scheduled-jobs/form/duplicate-config-alert';
import { PartialMostScheduledJobListType } from 'src/components/most/list/scheduled-jobs/types';
import { parseCron } from 'src/components/most/list/scheduled-jobs/utils/cron';

const getFormData = (
  entity?: PartialMostScheduledJobListType,
  defaultValues?: Partial<MostScheduledJob>,
) => {
  const { schedule: entitySchedule, jobConfig: entityJobConfig } = entity || {};

  let schedule: MostJobSchedule = { cron: { recurring: false } };
  let jobConfig: MostJobConfig = {} as any;

  if (defaultValues?.schedule) {
    schedule = defaultValues.schedule;
  } else if (entity?.schedule) {
    const parsedCronExpr = entitySchedule?.cron
      ? parseCron(entitySchedule.cron)
      : undefined;
    const scheduleNextRunAtObj = entitySchedule?.nextRunAt
      ? dayjs(entitySchedule.nextRunAt)
      : null;

    schedule = {
      cron: {
        date: scheduleNextRunAtObj,
        time: scheduleNextRunAtObj,
        recurring: !!entitySchedule?.cron,
        recurringType: parsedCronExpr?.type,
        recurringValue: parsedCronExpr?.value,
      },
    };
  }

  if (defaultValues?.jobConfig) {
    jobConfig = defaultValues.jobConfig;
  } else if (entityJobConfig) {
    jobConfig = {
      command: {
        commandId: entityJobConfig.commandId as string,
        name: entityJobConfig.name as string,
        type: entityJobConfig.type,
        description: entityJobConfig.description as string,
      },
      devices: entityJobConfig.devices as string[],
      storeIds: entityJobConfig.stores?.map((store) => store.id) as string[],
    } as MostJobConfig;
  }

  return {
    schedule,
    jobConfig,
  };
};

export const ActionConfigForm = ({
  entity,
  disabledFields = {},
  onSubmit,
  onClose,
  loading,
  title,
  calloutTitle,
  submitButtonLabel,
  defaultValues,
  hiddenFields,
  showDetails = false,
}: MostActionConfigJobFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list.schedule.form',
  });
  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
  } = useForm<Form>();

  const storeIds = watch('jobConfig.storeIds');
  const command = watch('jobConfig.command');

  useEffect(() => {
    reset(getFormData(entity, defaultValues));
  }, [reset, entity, defaultValues]);

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const jobConfig = watch('jobConfig') || {};

  const titleElement = title || t('create-schedule');
  const calloutTitleElement = calloutTitle || (
    <Typography variant="subtitle1">{t('callout.title')}</Typography>
  );
  const calloutColor = 'info';
  const callout = (
    <Callout icon={false} className="mb-4" severity={calloutColor}>
      {calloutTitleElement}
    </Callout>
  );

  return (
    <PageModal open={true} onClose={onCancel} title={titleElement} fullWidth>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          {!hiddenFields?.includes('callout') && callout}
          {!hiddenFields?.includes('storeIds') && (
            <div className="mb-4">
              <Typography
                className={clsx('flex', 'items-center', 'font-bold', 'mb-2')}
                color="var(--mui-palette-primary-900)"
                variant="subtitle1"
              >
                <Home className={clsx('mr-2', styles.iconColor)} />
                {t('fields.store-title')}
              </Typography>
              <Controller
                name="jobConfig.storeIds"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="mb-2">
                    <Typography
                      className="mb-2"
                      variant="subtitle1"
                      color={!!errors.jobConfig?.storeIds ? 'red' : ''}
                    >
                      {t('fields.stores')}
                      <span className="text-red-500"> *</span>
                    </Typography>
                    <StoresField
                      field={field}
                      disabled={disabledFields.storeIds}
                      allowSelectAll={true}
                    />
                  </div>
                )}
              />
            </div>
          )}

          <div className="mb-4">
            <Typography
              className={clsx('flex', 'items-center', 'font-bold', 'mb-2')}
              color="var(--mui-palette-primary-900)"
              variant="subtitle1"
            >
              <Settings className={clsx('mr-2', styles.iconColor)} />
              {t('fields.action-title')}
            </Typography>
            <Controller
              name="jobConfig.command"
              control={control}
              rules={{
                required: true,
                validate: (command) => {
                  if (typeof command === 'undefined') {
                    return false;
                  }

                  if (typeof command.commandId === 'undefined') {
                    return false;
                  }

                  if (typeof command.type === 'undefined') {
                    return false;
                  }
                },
              }}
              render={({ field }) => (
                <div className="mb-2">
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.jobConfig?.command ? 'red' : ''}
                  >
                    {t('fields.commandId')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <ActionField
                    field={field}
                    disabled={disabledFields.action}
                    command={jobConfig.command}
                    showDetails={showDetails}
                  />
                </div>
              )}
            />
            {!hiddenFields?.includes('devices') && (
              <Controller
                name="jobConfig.devices"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="mb-2">
                    <Typography
                      className="mb-2"
                      variant="subtitle1"
                      color={!!errors.jobConfig?.devices ? 'red' : ''}
                    >
                      {t('fields.devices')}
                      <span className="text-red-500"> *</span>
                    </Typography>
                    <DevicesField
                      field={field}
                      disabled={disabledFields.devices}
                      filter={{ storeIds, action: command }}
                    />
                  </div>
                )}
              />
            )}
          </div>

          <DuplicateConfigAlert
            commandId={jobConfig.command?.commandId}
            devices={jobConfig.devices}
          />

          {!hiddenFields?.includes('schedule') && (
            <div className="mb-4">
              <Typography
                className={clsx('flex', 'items-center', 'font-bold', 'mb-2')}
                color="var(--mui-palette-primary-900)"
                variant="subtitle1"
              >
                <Schedule className={clsx('mr-2', styles.iconColor)} />
                {t('fields.schedule-title')}
              </Typography>
              <Controller
                name="schedule.cron"
                control={control}
                rules={{
                  required: true,
                  validate: (cron) => {
                    if (typeof cron === 'undefined') {
                      return false;
                    }

                    if (typeof cron.date === 'undefined') {
                      return false;
                    }

                    if (typeof cron.time === 'undefined') {
                      return false;
                    }

                    if (cron.recurring) {
                      if (typeof cron.recurringType === 'undefined') {
                        return false;
                      }
                      if (typeof cron.recurringValue === 'undefined') {
                        return false;
                      }
                    }
                  },
                }}
                render={({ field }) => (
                  <div
                    className={clsx(
                      'grid',
                      'grid-cols-2',
                      'gap-4',
                      'auto-rows-max',
                    )}
                  >
                    <div className={clsx('col-span-2', 'sm:col-span-1')}>
                      <Typography
                        className="mb-2"
                        variant="subtitle1"
                        color={
                          !!errors.schedule?.cron &&
                          typeof field.value?.date === 'undefined'
                            ? 'red'
                            : ''
                        }
                      >
                        {t('fields.date')}
                        <span className="text-red-500"> *</span>
                      </Typography>
                      <DatePicker
                        value={field.value.date}
                        disabled={disabledFields.date}
                        disablePast={true}
                        className="w-full"
                        onChange={(data) => {
                          if (
                            !data?.isSame(field.value.date) &&
                            !dayjs().isSame(data, 'date')
                          ) {
                            field.onChange({
                              ...field.value,
                              date: data,
                            });
                            return;
                          }

                          field.onChange({
                            ...field.value,
                            date: data,
                            time: dayjs().isBefore(field.value.time, 'minutes')
                              ? field.value.time
                              : dayjs().endOf('day'),
                          });
                        }}
                      />
                    </div>
                    <div className={clsx('col-span-2', 'sm:col-span-1')}>
                      <Typography
                        className="mb-2"
                        variant="subtitle1"
                        color={
                          !!errors.schedule?.cron &&
                          typeof field.value?.time === 'undefined'
                            ? 'red'
                            : ''
                        }
                      >
                        {t('fields.time')}
                        <span className="text-red-500"> *</span>
                      </Typography>
                      <TimePicker
                        value={field.value.time}
                        disabled={disabledFields.time}
                        disablePast={dayjs().isSame(field.value.date, 'date')}
                        closeOnSelect={
                          !dayjs().isSame(field.value.date, 'date')
                        }
                        className="w-full"
                        onChange={(data) =>
                          field.onChange({
                            ...field.value,
                            time: data,
                          })
                        }
                      />
                    </div>
                    {!hiddenFields?.includes('recurring') &&
                      !disabledFields.recurring && (
                        <>
                          <div className={clsx('col-span-2', 'flex')}>
                            <Typography
                              className={clsx('mb-2', 'pt-3')}
                              variant="subtitle1"
                            >
                              {t('fields.recurring')}
                            </Typography>
                            <Switch
                              className="mr-4"
                              checked={field.value?.recurring || false}
                              onChange={(_event, checked) => {
                                field.onChange({
                                  ...field.value,
                                  recurring: checked,
                                });
                              }}
                            />
                          </div>
                          {field.value.recurring && (
                            <div
                              className={clsx(
                                'col-span-2',
                                'flex',
                                'gap-4',
                                'items-center',
                              )}
                            >
                              <Typography
                                className={clsx(
                                  'mb-2',
                                  'flex',
                                  'gap-1',
                                  'pt-2',
                                )}
                                variant="subtitle1"
                                color={
                                  !!errors.schedule?.cron &&
                                  (typeof field.value.recurringType ===
                                    'undefined' ||
                                    typeof field.value.recurringValue ===
                                      'undefined')
                                    ? 'red'
                                    : ''
                                }
                              >
                                {t('fields.cron.prefix')}
                                <span className="text-red-500"> *</span>
                              </Typography>
                              <CronField
                                field={field}
                                disabled={disabledFields.devices}
                                type={(field as any).value?.recurringType}
                              />
                            </div>
                          )}
                        </>
                      )}
                  </div>
                )}
              />
            </div>
          )}

          <div className="mb-4">
            <Typography variant="subtitle2" className="mb-2">
              <span className="text-red-500">* </span>
              {t('required-fields')}
            </Typography>
          </div>
          <Delimiter className="mt-6" />
          <BottomNavigation>
            <Button
              type="submit"
              variant="contained"
              className="mr-2"
              color="primary"
              size="medium"
              disabled={loading}
            >
              {submitButtonLabel ?? t('submit')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onClose?.()}
      />
    </PageModal>
  );
};
